import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';

/**
 * Menu contestuale leggero (self-contained, nessuna dipendenza Radix) per
 * le righe di `AdminClientsPage`/`AdminRegistrationsPage` — evita i
 * problemi di tipizzazione TS del componente shadcn `dropdown-menu.jsx`
 * (mai usato altrove in .tsx).
 *
 * Il popover è renderizzato via `createPortal` su `document.body` con
 * posizionamento `fixed` (calcolato dal bounding rect del bottone) — le
 * tabelle admin che lo ospitano hanno `overflow-x-auto` per lo scroll
 * orizzontale, che clippa implicitamente anche l'overflow verticale
 * (quirk CSS: se overflow-x è impostato, overflow-y "visible" diventa
 * "auto"), troncando un popover posizionato in modo relativo/assoluto
 * dentro quel contenitore (bug segnalato dall'utente 24/02/2026 su
 * Registrazioni). Il portal esce completamente da quella gerarchia.
 *
 * "Flip up": se il bottone è vicino al fondo della viewport e il menu
 * (altezza reale misurata dopo il render, variabile in base al numero di
 * voci) non ci sta sotto, si riposiziona sopra il bottone invece che
 * sotto — altrimenti un popover "fixed" può finire parzialmente/
 * completamente fuori dalla viewport e diventare invisibile, senza che
 * lo scroll della pagina possa mai rivelarlo (bug trovato da
 * testing_agent_v4, iteration_186.json).
 */
export function ClientActionsMenu({ children, testId }: { children: React.ReactNode; testId: string }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; bottom: number; right: number } | null>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [ready, setReady] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setAnchor({ top: rect.top, bottom: rect.bottom, right: rect.right });
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
      setReady(false);
    }
    setOpen(o => !o);
  };

  // Misura l'altezza reale del menu dopo il render e, se non c'è spazio
  // sufficiente sotto il bottone, lo riposiziona sopra (flip-up). Gira
  // prima del paint (useLayoutEffect), quindi non produce flicker visibile.
  useLayoutEffect(() => {
    if (!open || !anchor || !menuRef.current) return;
    const margin = 8;
    const menuHeight = menuRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - anchor.bottom - margin;
    const spaceAbove = anchor.top - margin;
    const top = menuHeight <= spaceBelow || spaceBelow >= spaceAbove
      ? Math.min(anchor.bottom + 4, window.innerHeight - menuHeight - margin)
      : Math.max(margin, anchor.top - menuHeight - 4);
    setPos({ top, right: window.innerWidth - anchor.right });
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Il menu è posizionato "fixed" in base al bounding rect del bottone
    // calcolato all'apertura — se la tabella scrolla (orizzontale o
    // verticale) o la finestra viene ridimensionata, il popover non
    // seguirebbe più il bottone: lo chiudiamo per evitare che resti
    // "staccato" a schermo.
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        className="h-8 w-8 rounded-lg border flex items-center justify-center hover:bg-gray-50 transition-colors"
        data-testid={testId}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, right: pos.right, visibility: ready ? 'visible' : 'hidden' }}
          className="z-50 w-52 rounded-lg border bg-white shadow-lg py-1"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}

export function ClientActionsMenuItem({
  onClick, to, testId, children,
}: { onClick?: () => void; to?: string; testId: string; children: React.ReactNode }) {
  const className = 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors';
  if (to) {
    return <Link to={to} className={className} data-testid={testId}>{children}</Link>;
  }
  return (
    <button onClick={onClick} className={className} data-testid={testId}>
      {children}
    </button>
  );
}
